/**
 * My Suggestions — the user-facing surface for every authenticated
 * REEDOS user (including role-restricted personas like data_sales,
 * data_user and logo_user).
 *
 * Route: /workspace/suggestions
 * Auth : any authenticated user (no capability gate).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, Lightbulb, Plus, ClipboardText } from "@phosphor-icons/react";

import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import SuggestionFormDialog from "./SuggestionFormDialog";


const STATUS_META = {
    open: { label: "Received", tone: "bg-slate-100 text-slate-700" },
    under_review: { label: "Under review", tone: "bg-amber-100 text-amber-800" },
    converted: { label: "Converted to Project", tone: "bg-emerald-100 text-emerald-800" },
    not_planned: { label: "Not planned", tone: "bg-rose-100 text-rose-800" },
};

const CATEGORY_META = {
    bug: { label: "Bug", tone: "bg-rose-50 text-rose-700 border-rose-200" },
    improvement: { label: "Improvement", tone: "bg-blue-50 text-blue-700 border-blue-200" },
    feature: { label: "Feature", tone: "bg-violet-50 text-violet-700 border-violet-200" },
};


function StatusBadge({ status }) {
    const m = STATUS_META[status] || { label: status, tone: "bg-slate-100 text-slate-700" };
    return (
        <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${m.tone}`}
            data-testid={`sgn-row-status-${status}`}
        >
            {m.label}
        </span>
    );
}


function CategoryBadge({ category }) {
    const m = CATEGORY_META[category] || { label: category, tone: "" };
    return (
        <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs ${m.tone}`}>
            {m.label}
        </span>
    );
}


function formatWhen(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
        });
    } catch { return iso; }
}


export default function MySuggestions() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/suggestions/mine?limit=200");
            setItems(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err) || "Failed to load your suggestions");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const stats = useMemo(() => {
        const s = { open: 0, under_review: 0, converted: 0, not_planned: 0 };
        items.forEach((it) => {
            if (s[it.status] !== undefined) s[it.status]++;
        });
        return s;
    }, [items]);

    return (
        <div className="space-y-6" data-testid="my-suggestions-page">
            <PageHeader
                eyebrow="MY WORKSPACE"
                title="My Suggestions"
                description="Bugs, ideas and feature requests you have submitted.  A super admin reviews and responds."
                actions={
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={load}
                            disabled={loading}
                            data-testid="sgn-refresh-btn"
                        >
                            <ArrowClockwise className="mr-1.5 h-4 w-4" />
                            Refresh
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => setFormOpen(true)}
                            data-testid="sgn-new-btn"
                        >
                            <Plus className="mr-1.5 h-4 w-4" />
                            New Suggestion
                        </Button>
                    </div>
                }
            />

            {/* Stat pills */}
            <div className="flex flex-wrap gap-2 text-sm">
                <Badge variant="outline">Received · {stats.open}</Badge>
                <Badge variant="outline">Under review · {stats.under_review}</Badge>
                <Badge variant="outline">Converted · {stats.converted}</Badge>
                <Badge variant="outline">Not planned · {stats.not_planned}</Badge>
            </div>

            {loading ? (
                <div className="rounded-lg border bg-white p-8 text-center text-sm text-muted-foreground">
                    Loading your suggestions…
                </div>
            ) : items.length === 0 ? (
                <EmptyState
                    icon={<Lightbulb className="h-10 w-10" />}
                    title="No suggestions yet"
                    description="Have an idea, spotted a bug, or want to improve a workflow? Submit your first suggestion — every voice counts."
                    action={
                        <Button
                            onClick={() => setFormOpen(true)}
                            data-testid="sgn-empty-new-btn"
                        >
                            <Plus className="mr-1.5 h-4 w-4" />
                            Suggest an improvement
                        </Button>
                    }
                    data-testid="my-suggestions-empty"
                />
            ) : (
                <div className="overflow-hidden rounded-lg border bg-white">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                            <tr>
                                <th className="px-4 py-2">Title</th>
                                <th className="px-4 py-2">Category</th>
                                <th className="px-4 py-2">Status</th>
                                <th className="px-4 py-2">Submitted</th>
                                <th className="px-4 py-2">Reviewer note</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {items.map((it) => (
                                <tr key={it.id} data-testid={`sgn-row-${it.id}`}>
                                    <td className="px-4 py-3 align-top">
                                        <div className="font-medium">{it.title}</div>
                                        <div className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                                            {it.description}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        <CategoryBadge category={it.category} />
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        <StatusBadge status={it.status} />
                                        {it.converted_to_workflow_instance_id && (
                                            <div className="mt-1 flex items-center gap-1 text-xs text-emerald-700">
                                                <ClipboardText className="h-3.5 w-3.5" />
                                                <span className="font-mono">
                                                    {it.converted_to_workflow_instance_id}
                                                </span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                                        {formatWhen(it.submitted_at)}
                                    </td>
                                    <td className="px-4 py-3 align-top text-xs text-slate-600">
                                        {it.reviewer_note || "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <SuggestionFormDialog
                open={formOpen}
                onOpenChange={setFormOpen}
                onCreated={() => load()}
            />
        </div>
    );
}
